/**
 * Copy and constants for the Get a Quote wizard.
 */

import { MONDU_HANDOFF_MODAL_CONTENT } from '../content/sharedContent';
import type { MonthlyInvoiceVolumeId } from './quoteWizardState';

export const WIZARD_CONTENT = {
  title: 'Get A Quote',
  subtitleEmphasis: 'No commitment, no cost',
  subtitle: ' - just a personalized quote on your invoices for all your customers.',
  actions: {
    back: 'Back',
    next: 'Next',
    generateQuote: 'Generate Quote',
    modifyCustomerSelection: 'Modify Customer Selection',
  },
  steps: [
    { index: 1, label: 'Business Details' },
    { index: 2, label: 'Add Customers' },
    { index: 3, label: 'Review Quote' },
  ],
  /**
   * Shown in the wizard footer when a supplier who skipped the consent modal has a quote request fail.
   * Same source as the modal's copy, so the two surfaces cannot drift apart. Only `description` is
   * rendered there — see `GetAQuoteWizard`.
   */
  requestError: MONDU_HANDOFF_MODAL_CONTENT.requestError,
} as const;

/**
 * Approved annual revenue bands, in menu order. Each closed band sends its midpoint. `Over $250M`
 * sends one dollar above its floor, so it stays above `$100M - $250M` even if that band claims
 * exactly $250M. The frame drew the second band as `$10 - $25M`; the M after 10 is required.
 */
export const ANNUAL_REVENUE_BANDS = [
  { id: '5M-10M', label: '$5M - $10M', figure: 7_500_000 },
  { id: '10M-25M', label: '$10M - $25M', figure: 17_500_000 },
  { id: '25M-50M', label: '$25M - $50M', figure: 37_500_000 },
  { id: '50M-100M', label: '$50M - $100M', figure: 75_000_000 },
  { id: '100M-250M', label: '$100M - $250M', figure: 175_000_000 },
  { id: '250M-PLUS', label: 'Over $250M', figure: 250_000_001 },
] as const satisfies readonly { id: string; label: string; figure: number }[];

export const BUSINESS_DETAILS_CONTENT = {
  heading: 'Your Business Details',
  description: 'To build an accurate quote, provide your company details.',
  fields: {
    firstName: { label: 'First Name' },
    lastName: { label: 'Last Name' },
    emailAddress: { label: 'Email Address' },
    phoneNumber: { label: 'Phone Number' },
    companyName: { label: 'Supplier Account Name' },
    addressLine1: { label: 'US Registered Address' },
    city: { label: 'City' },
    state: { label: 'State', placeholder: 'Select' },
    zipCode: { label: 'Zip Code' },
    revenueRange: { label: 'Your Annual Revenue Range (in USD)', placeholder: 'Select Range' },
  },
  errors: {
    firstName: 'Enter your first name.',
    lastName: 'Enter your last name.',
    emailAddress: 'Enter a valid email address.',
    phoneNumber: 'Enter a valid phone number.',
    companyName: 'Enter your supplier account name.',
    addressLine1: 'Enter your US registered address.',
    city: 'Enter a city.',
    state: 'Select a state.',
    zipCode: 'Enter a valid 5-digit zip code.',
    revenueRange: 'Select an annual revenue range.',
  },
} as const;

/**
 * Placeholder bands, invented so the control is real enough to drive the Added Customers row figure.
 *
 * Product and Mondu have not agreed these, and the value is not sent on the quotes POST. Do not treat
 * a selection as a number: when the real list is defined it starts from the annual revenue ranges ÷ 12
 * (see the hub plan), which these do not follow.
 */
export const MONTHLY_INVOICE_VOLUMES: { id: MonthlyInvoiceVolumeId; label: string }[] = [
  { id: 'LT-100K', label: '< $100K' },
  { id: '100K-500K', label: '$100K - $500K' },
  { id: '500K-1M', label: '$500K - $1M' },
  { id: '1M-5M', label: '$1M - $5M' },
  { id: '5M-PLUS', label: '$5M+' },
];

/** Null for a customer added without picking a band, which leaves the row's figure off. */
export const monthlyInvoiceVolumeLabel = (id: MonthlyInvoiceVolumeId | null): string | null =>
  MONTHLY_INVOICE_VOLUMES.find((volume) => volume.id === id)?.label ?? null;

export const MANUAL_CUSTOMERS_CONTENT = {
  heading: 'Add Customers Manually',
  description: 'Search the shared Mondu legal entity database to add the customers you invoice.',
  fields: {
    entityName: {
      label: 'Search Customer Entity Name',
      /** Invented copy — not written or reviewed by content. Expect a rewrite; see the slice plan. */
      helper: "Pick a customer from the list, or type the full legal name if it isn't there.",
      /** Invented, like `helper`. Not an error: the name is added and sent to Mondu either way. */
      noResults: "No match on file. We'll send this name to Mondu to match.",
    },
    monthlyVolume: {
      label: 'Monthly Invoice Volume',
      placeholder: 'Select Range',
      helper: 'USD only',
    },
  },
  errors: {
    duplicate: 'Customer already added',
  },
  actions: {
    addCustomer: 'Add Customer',
    /** Composed with the customer name so each row's control is distinguishable. */
    remove: 'Remove',
  },
  addedCustomers: {
    heading: 'Added Customers',
    /**
     * Invented copy — not written or reviewed by content. Expect a rewrite; see the slice plan.
     * Stands in for the address line on a typed row.
     */
    unmatched: "Not on file — we'll ask Mondu to match this name",
  },
  skip: {
    heading: 'Not ready to add your customers?',
    text: 'Proceed to see a price range based on your entered data only.',
  },
  /**
   * As the frame writes it. Differs from the shared pricing disclaimer in its last word only —
   * `fundable` here, `eligible` there. Kept separate rather than reconciled, since neither wording
   * is mine to change; see the slice plan's open item. Step 1 no longer renders the shared one.
   */
  disclaimer:
    'Pricing ranges shown are directional only and subject to underwriting by J.P. Morgan and their service provider, Mondu. Not every customer will be fundable.',
} as const;

/**
 * Consent modal shown before the quotes POST.
 *
 * The privacy sentence and the request-error wording are the handoff modal's, since both modals ask
 * for the same consent and fail the same way. The rest differs: this one never leaves the site, so
 * there is no third-party destination disclaimer and no redirect note.
 */
export const GET_A_QUOTE_MODAL_CONTENT = {
  title: 'Get A Quote',
  disclaimer: 'Share details with Mondu to provide a quote',
  consent: MONDU_HANDOFF_MODAL_CONTENT.consent,
  requestError: MONDU_HANDOFF_MODAL_CONTENT.requestError,
  /** Invented copy — not written or reviewed by content. Mirrors the handoff modal's message. */
  impersonationMessage: 'The supplier must generate the quote themselves so they can provide consent.',
  cancelLabel: 'Cancel',
  proceedLabel: 'Acknowledge & Proceed',
} as const;
